import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import './EditorStyles.css';
import './CVEditorStyles.css';

const CVEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [documentData, setDocumentData] = useState(location.state?.documentData || null);
  const [loading, setLoading] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [editingExpIndex, setEditingExpIndex] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [graphFeedback, setGraphFeedback] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const editTextareaRef = useRef(null);

  useEffect(() => {
    if (!documentData) {
      navigate('/application-builder/form/cv');
    } else {
      setExperiences(documentData.experiences || []);
    }
  }, [documentData, navigate]);

  const openAIModal = (field, index = null) => {
    setEditingField(field);
    setEditingIndex(index);
    setAiModalOpen(true);
    setAiPrompt('');
    setAiSuggestion(null);
  };

  const closeAIModal = () => {
    setAiModalOpen(false);
    setEditingField(null);
    setEditingIndex(null);
    setAiPrompt('');
    setAiSuggestion(null);
  };

  const getAISuggestion = async () => {
    if (editingField === null) return;
    let currentText = '';
    if (editingIndex !== null && editingField === 'experience_summary') {
      currentText = experiences[editingIndex]?.ai_generated_summary || experiences[editingIndex]?.summary || '';
    } else {
      currentText = documentData?.[editingField] || '';
    }
    setAiLoading(true);
    try {
      const response = await applicationBuilderAPI.improveText(
        currentText,
        'experience_summary',
        aiPrompt || 'Make it more impactful'
      );
      if (response.improved_text) setAiSuggestion(response.improved_text);
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      alert('Failed to get AI suggestion. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const acceptSuggestion = () => {
    if (!aiSuggestion || editingField === null) return;
    if (editingIndex !== null && editingField === 'experience_summary') {
      const newExp = [...experiences];
      newExp[editingIndex] = { ...newExp[editingIndex], ai_generated_summary: aiSuggestion };
      setExperiences(newExp);
    }
    closeAIModal();
  };

  const startEditExperience = (index) => {
    setEditingExpIndex(index);
  };

  const saveEditExperience = (index, newText) => {
    const newExp = [...experiences];
    newExp[index] = { ...newExp[index], ai_generated_summary: newText };
    setExperiences(newExp);
    setEditingExpIndex(null);
  };

  const handleGraphFeedback = async () => {
    setGraphLoading(true);
    try {
      const expText = (experiences || [])
        .map(e => `${e.title || e.role || ''} at ${e.company || ''}. ${e.ai_generated_summary || e.summary || ''}`)
        .filter(Boolean)
        .join('\n');
      const eduText = (documentData?.education || [])
        .map(ed => `${ed.degree_type || ''} ${ed.field || ''} at ${ed.school || ''}. ${ed.courses || ''}`)
        .filter(Boolean)
        .join('\n');
      const skillsText = documentData?.skills_data
        ? [
            documentData.skills_data.languages_fluent,
            documentData.skills_data.technical_skills,
            documentData.skills_data.certifications,
            documentData.skills_data.activities,
            documentData.skills_data.interests,
          ].filter(Boolean).join('. ')
        : '';
      const fullText = [expText, eduText, skillsText].filter(Boolean).join('\n\n');

      const documentPayload = {
        ...(documentData || {}),
        experiences,
        content: fullText,
        skills: skillsText,
      };
      const response = await applicationBuilderAPI.getGraphFeedback({
        elementId: 'cv',
        content: fullText,
        document: documentPayload,
      });
      setGraphFeedback(response);
    } catch (error) {
      console.error('CV graph feedback error:', error);
      const msg = error.response?.data?.feedback || error.response?.data?.error || error.message || 'Failed to get graph feedback. Please try again.';
      alert(msg);
    } finally {
      setGraphLoading(false);
    }
  };

  const handleDownload = () => {
    const documentId = documentData?.cv_docx;
    if (!documentId) {
      alert('No file available to download.');
      return;
    }
    setLoading(true);
    applicationBuilderAPI.openBuilderDocument(documentId)
      .catch((error) => {
        console.error('Error downloading CV:', error);
        alert(error?.message === 'popup'
          ? 'Allow pop-ups for this site to open documents.'
          : 'Failed to download CV. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const getCurrentTextForModal = () => {
    if (editingIndex !== null && editingField === 'experience_summary') {
      return experiences[editingIndex]?.ai_generated_summary || experiences[editingIndex]?.summary || '';
    }
    return documentData?.[editingField] || '';
  };

  if (!documentData) {
    return <div className="editor-container"><div className="editor-loading">Loading...</div></div>;
  }

  const contactParts = [documentData.email, documentData.phone, documentData.physical_address].filter(Boolean);
  const contactLine = contactParts.join(' • ');
  const skills = documentData.skills_data || {};

  return (
    <div className="editor-container cv-editor-container">
      <div className="editor-wrapper">
        <div className="editor-header">
          <h1>CV / Resume Editor</h1>
          <p>Review and edit your CV. Use EDIT to change text and AI to improve it.</p>
        </div>
        <div className="editor-actions">
          <button type="button" onClick={() => navigate('/application-builder/form/cv')} className="edit-button">Edit Form</button>
          <button type="button" onClick={handleGraphFeedback} className="edit-button" disabled={graphLoading}>
            {graphLoading ? 'Scoring…' : 'Get Graph Feedback'}
          </button>
          <button type="button" onClick={handleDownload} className="download-button" disabled={loading}>
            {loading ? 'Downloading...' : 'Download CV'}
          </button>
        </div>

        {graphFeedback?.feedback && (
          <div className="ps-feedback-box" style={{ margin: '1rem 0', padding: '1rem', border: '1px solid var(--iv-line)', borderRadius: 8 }}>
            <div className="ps-feedback-label">
              Graph feedback
              {graphFeedback.isomorphism?.band ? ` · ${graphFeedback.isomorphism.band}` : ''}
              {graphFeedback.db_write?.status ? ` · saved: ${graphFeedback.db_write.status}` : ''}
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{graphFeedback.feedback}</pre>
          </div>
        )}

        <div className="document-preview cv-preview">
          <div className="preview-content cv-preview-content">
            <h2 className="cv-preview-name">{documentData.full_name}</h2>
            {contactLine && <p className="cv-preview-contact">{contactLine}</p>}

            <h3 className="cv-preview-section-title">Education</h3>
            {(documentData.education || []).map((edu, idx) => (
              <div key={idx} className="cv-preview-education">
                <div className="cv-preview-edu-row">
                  <span>{[edu.degree_type, edu.field].filter(Boolean).join(' in ')}</span>
                  <span className="cv-preview-dates">{edu.start && edu.end ? `${edu.start} - ${edu.end}` : ''}</span>
                </div>
                {edu.school && <p className="cv-preview-muted">{[edu.school, edu.city].filter(Boolean).join(', ')}</p>}
                {edu.gpa && <p>GPA: {edu.gpa}</p>}
              </div>
            ))}

            <h3 className="cv-preview-section-title">Experience</h3>
            {experiences.map((exp, idx) => (
              <div key={idx} className="cv-preview-experience">
                <div className="cv-preview-exp-row">
                  <strong>{[exp.title, exp.group].filter(Boolean).join(', ')}</strong>
                  <span className="cv-preview-dates">{exp.start && exp.end ? `${exp.start} - ${exp.end}` : ''}</span>
                </div>
                <p className="cv-preview-muted">{[exp.company, exp.city, exp.country].filter(Boolean).join(', ')}</p>
                <div className="cv-preview-bullets">
                  {editingExpIndex === idx ? (
                    <div className="cv-preview-edit-block">
                      <textarea
                        ref={editTextareaRef}
                        className="cv-preview-textarea"
                        defaultValue={exp.ai_generated_summary || exp.summary || ''}
                        onKeyDown={(e) => { if (e.key === 'Escape') setEditingExpIndex(null); }}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="cv-preview-done-btn"
                        onClick={() => {
                          const value = editTextareaRef.current?.value ?? (exp.ai_generated_summary || exp.summary || '');
                          saveEditExperience(idx, value);
                        }}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <>
                      {(exp.ai_generated_summary || exp.summary || '')
                        .split(/\n+/)
                        .filter(Boolean)
                        .map((line, i) => (
                          <p key={i}>{line.trim().replace(/^[-•]\s*/, '')}</p>
                        ))}
                      <div className="cv-preview-bullet-actions">
                        <button type="button" className="cv-btn-edit" onClick={() => startEditExperience(idx)}>EDIT</button>
                        <button type="button" className="cv-btn-ai" onClick={() => openAIModal('experience_summary', idx)}>AI</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            <h3 className="cv-preview-section-title">Skills, Activities &amp; Interests</h3>
            <div className="cv-preview-skills">
              {skills.technical_skills && <p><strong>Technical Skills:</strong> {skills.technical_skills}</p>}
              {skills.activities && <p><strong>Activities:</strong> {skills.activities}</p>}
              {skills.interests && <p><strong>Interests:</strong> {skills.interests}</p>}
              {skills.languages_fluent && <p><strong>Languages:</strong> {skills.languages_fluent}</p>}
              {skills.certifications && <p><strong>Certifications:</strong> {skills.certifications}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* AI Writing Assistant modal */}
      {aiModalOpen && (
        <div className="modal-overlay" onClick={closeAIModal}>
          <div className="modal-content ai-assistant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✨ AI Writing Assistant</h3>
              <button type="button" className="close-btn" onClick={closeAIModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="current-text">
                <div className="text-label">CURRENT TEXT:</div>
                <div className="text-preview ai-current-text">{getCurrentTextForModal() || 'No content'}</div>
              </div>
              <div className="ai-input">
                <label className="ai-prompt-label">How would you like to improve this section? (e.g., &apos;Make it more impactful&apos;, &apos;Add specific metrics&apos;, &apos;Make it more concise&apos;)</label>
                <input
                  type="text"
                  className="ai-prompt-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Make it more impactful"
                />
                <button type="button" className="btn btn-primary get-ai-btn" onClick={getAISuggestion} disabled={aiLoading}>
                  {aiLoading ? 'Getting suggestion...' : 'Get AI Suggestion'}
                </button>
              </div>
              {aiSuggestion && (
                <div className="ai-suggestion">
                  <div className="text-label">AI SUGGESTION:</div>
                  <div className="text-preview suggested ai-suggestion-box">{aiSuggestion}</div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeAIModal}>Keep Original</button>
                    <button type="button" className="btn btn-primary" onClick={acceptSuggestion}>Accept Suggestion</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CVEditor;
